/**
 * t705u: the people picker's logic, shared by the web AudiencePicker and the phone AudienceSheet. A choice is people
 * by name plus whole departments, groups, positions and buildings. Nothing is suggested until you type; what's already
 * picked is left out of the results and shown as chips instead.
 */
export type AudiencePickKind = 'person' | 'department' | 'group' | 'position' | 'building';
export interface AudiencePick { people: string[]; teams: string[]; positions?: string[]; buildings?: string[] }
export interface AudiencePickLookups {
  members: readonly { user_id: string; display_name: string; email?: string | null; job_title?: string | null; status?: string | null }[];
  teams: readonly { id: string; name: string; kind?: string | null }[];
  positions: readonly { id: string; name: string; archived_at?: string | null }[];
  buildings: readonly { id: string; name: string; code?: string | null; archived_at?: string | null }[];
}
export interface AudiencePickOption { kind: AudiencePickKind; id: string; name: string }

export const AUDIENCE_PICK_HEADINGS: Record<AudiencePickKind, string> = {
  person: 'People', department: 'Departments', group: 'Groups', position: 'Positions', building: 'Buildings',
};

export const emptyAudiencePick = (): AudiencePick => ({ people: [], teams: [], positions: [], buildings: [] });
export const audiencePickCount = (v: AudiencePick) => v.people.length + v.teams.length + (v.positions?.length ?? 0) + (v.buildings?.length ?? 0);

const teamKind = (t: { kind?: string | null }): AudiencePickKind => (t.kind === 'group' ? 'group' : 'department');
const listOf = (v: AudiencePick, kind: AudiencePickKind): string[] =>
  kind === 'person' ? v.people : kind === 'position' ? v.positions ?? [] : kind === 'building' ? v.buildings ?? [] : v.teams;

export function audiencePickHas(v: AudiencePick, kind: AudiencePickKind, id: string) { return listOf(v, kind).includes(id); }

/** Adds the option, or takes it off when it's already picked. */
export function audiencePickToggle(v: AudiencePick, kind: AudiencePickKind, id: string): AudiencePick {
  const cur = listOf(v, kind);
  const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
  if (kind === 'person') return { ...v, people: next };
  if (kind === 'position') return { ...v, positions: next };
  if (kind === 'building') return { ...v, buildings: next };
  return { ...v, teams: next };
}

/** Everything that could be picked: active people, live positions and buildings. */
export function audiencePickOptions(l: AudiencePickLookups, kinds: readonly AudiencePickKind[]): AudiencePickOption[] {
  const want = new Set(kinds);
  return [
    ...(want.has('person') ? l.members.filter(m => !m.status || m.status === 'active').map(m => ({ kind: 'person' as const, id: m.user_id, name: m.display_name })) : []),
    ...l.teams.filter(t => want.has(teamKind(t))).map(t => ({ kind: teamKind(t), id: t.id, name: t.name })),
    ...(want.has('position') ? l.positions.filter(p => !p.archived_at).map(p => ({ kind: 'position' as const, id: p.id, name: p.name })) : []),
    ...(want.has('building') ? l.buildings.filter(b => !b.archived_at).map(b => ({ kind: 'building' as const, id: b.id, name: b.name })) : []),
  ];
}

/**
 * Search results by heading, in the order of `kinds`. Empty until something is typed. Matches names (and a person's
 * email and job title, a building's code); picked options and ones `allow` refuses are left out.
 */
export function audiencePickSearch(term: string, l: AudiencePickLookups, v: AudiencePick, kinds: readonly AudiencePickKind[],
  allow?: (kind: AudiencePickKind, id: string) => boolean, limit = 8): { kind: AudiencePickKind; heading: string; items: AudiencePickOption[] }[] {
  const t = term.trim().toLowerCase();
  if (!t) return [];
  const extra = new Map<string, string>();
  for (const m of l.members) extra.set(`person:${m.user_id}`, `${m.email ?? ''} ${m.job_title ?? ''}`);
  for (const b of l.buildings) if (b.code) extra.set(`building:${b.id}`, b.code);
  const hits = audiencePickOptions(l, kinds).filter(o => !audiencePickHas(v, o.kind, o.id) && (!allow || allow(o.kind, o.id))
    && `${o.name} ${extra.get(`${o.kind}:${o.id}`) ?? ''}`.toLowerCase().includes(t));
  return kinds.map(kind => ({ kind, heading: AUDIENCE_PICK_HEADINGS[kind], items: hits.filter(o => o.kind === kind).slice(0, kind === 'person' ? limit * 2 : limit) }))
    .filter(g => g.items.length > 0);
}

/** The picked options as chips: departments and groups, buildings, positions, then people (in the order picked). */
export function audiencePickChips(v: AudiencePick, l: AudiencePickLookups): AudiencePickOption[] {
  const team = new Map(l.teams.map(t => [t.id, t]));
  const name = (list: readonly { id: string; name: string }[], id: string) => list.find(x => x.id === id)?.name;
  return [
    ...v.teams.map(id => ({ kind: team.has(id) ? teamKind(team.get(id)!) : 'department' as AudiencePickKind, id, name: team.get(id)?.name ?? 'Team' })),
    ...(v.buildings ?? []).map(id => ({ kind: 'building' as const, id, name: name(l.buildings, id) ?? 'Building' })),
    ...(v.positions ?? []).map(id => ({ kind: 'position' as const, id, name: name(l.positions, id) ?? 'Position' })),
    ...v.people.map(id => ({ kind: 'person' as const, id, name: l.members.find(m => m.user_id === id)?.display_name ?? 'Someone' })),
  ];
}

/** "Warehouse, Tom Evans +2", or `empty` when nothing is picked. */
export function audiencePickSummary(v: AudiencePick, l: AudiencePickLookups, empty: string, shown = 2): string {
  const chips = audiencePickChips(v, l);
  if (!chips.length) return empty;
  const head = chips.slice(0, shown).map(c => c.name).join(', ');
  return chips.length > shown ? `${head} +${chips.length - shown}` : head;
}

/**
 * t681u: who an invite reaches. People picked by name, plus everyone now in a picked department or group, or working
 * at a picked building or in a picked position (from the staff directory), each person once.
 */
export interface InviteeChoice { people: string[]; teams: string[]; buildings?: string[]; positions?: string[] }
export interface InviteeStaff { user_id: string; location_ids: string[]; position_ids: string[]; team_ids?: string[] }
export function expandInvitees(v: InviteeChoice, staff: readonly InviteeStaff[], teamMembers: (teamId: string) => string[] = () => []): string[] {
  const out = new Set(v.people);
  const teams = new Set(v.teams); const bs = new Set(v.buildings ?? []); const ps = new Set(v.positions ?? []);
  for (const t of v.teams) for (const id of teamMembers(t)) out.add(id);
  for (const s of staff) {
    if (s.location_ids.some(l => bs.has(l)) || s.position_ids.some(p => ps.has(p)) || (s.team_ids ?? []).some(t => teams.has(t))) out.add(s.user_id);
  }
  return [...out];
}
